import {
  faDownload,
  faMagnifyingGlass,
  faPlus,
  faSliders,
  faTableColumns,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useParams, useRouteMatch } from 'react-router-dom';

import {
  Breadcrumbs,
  Button,
  CardV2,
  Heading,
  Input,
  PageHeader,
  Table,
  Text,
  toast,
  Toolbar,
} from '@feathr/components';

import donationTransactionColumns from '../columns/donationTransactionColumns';
import formColumns from '../columns/formColumns';
import FormRowActions from '../components/FormRowActions';
import PrototypeDoneModal from '../components/PrototypeDoneModal';
import type { ISampleDonationTransaction, ISampleForm } from '../sampleData';
import { sampleDonationForms, sampleDonationTransactions } from '../sampleData';
import { buildPrototypeBreadcrumbs } from '../utils/buildBreadcrumbs';

import * as shellStyles from '../Shell/PrototypeShell.css';

const FUNDRAISING_TABS = ['activity', 'forms', 'settings'] as const;
type TFundraisingTab = (typeof FUNDRAISING_TABS)[number];

function isFundraisingTab(value: string | undefined): value is TFundraisingTab {
  return FUNDRAISING_TABS.includes(value as TFundraisingTab);
}

function getTitle(t: TFunction, tab: TFundraisingTab): string {
  const titles: Record<TFundraisingTab, string> = {
    activity: t('Fundraising activity'),
    forms: t('Donation forms'),
    settings: t('Fundraising settings'),
  };
  return titles[tab];
}

interface ISettingSection {
  description: string;
  name: string;
}

function FundraisingPage(): JSX.Element {
  const { t } = useTranslation();
  const { tab: tabParam } = useParams<{ tab: string }>();

  if (!isFundraisingTab(tabParam)) {
    return <Redirect to={'../activity'} />;
  }

  return <FundraisingTabPage t={t} tab={tabParam} />;
}

interface IFundraisingTabPageProps {
  t: TFunction;
  tab: TFundraisingTab;
}

function FundraisingTabPage({ t, tab }: Readonly<IFundraisingTabPageProps>): JSX.Element {
  const theme = useMantineTheme();
  const match = useRouteMatch<{ accountId: string }>('/:accountId/ia-prototype');
  const base = match?.url ?? '';
  const [search, setSearch] = useState<string>('');
  const [isDoneOpen, setDoneOpen] = useState<boolean>(false);
  const [doneTitle, setDoneTitle] = useState<string>('');

  const breadcrumbItems = buildPrototypeBreadcrumbs(
    base,
    { label: t('Fundraising'), defaultPath: '/fundraising/activity' },
    getTitle(t, tab),
    `/fundraising/${tab}`,
  );

  const lower = search.trim().toLowerCase();

  const transactionCols = useMemo(() => donationTransactionColumns({ base, t }), [base, t]);

  const formCols = useMemo(
    () =>
      formColumns({
        base,
        t,
        onAction: (form, action): void => {
          if (action === 'duplicate') {
            toast(t('Duplicated "{{name}}".', { name: form.name }), { type: 'success' });
            return;
          }
          setDoneTitle(t('Edit donation form'));
          setDoneOpen(true);
        },
        renderActions: (form, onAction): JSX.Element => (
          <FormRowActions form={form} onAction={onAction} />
        ),
      }),
    [base, t],
  );

  const filteredTransactions = useMemo<ISampleDonationTransaction[]>(
    () =>
      lower
        ? sampleDonationTransactions.filter(
            (txn) =>
              txn.donor.toLowerCase().includes(lower) || txn.formName.toLowerCase().includes(lower),
          )
        : sampleDonationTransactions,
    [lower],
  );

  const filteredForms = useMemo<ISampleForm[]>(
    () =>
      lower
        ? sampleDonationForms.filter((f) => f.name.toLowerCase().includes(lower))
        : sampleDonationForms,
    [lower],
  );

  const totalDonationVolume = useMemo<number>(
    () =>
      sampleDonationTransactions
        .filter((txn) => txn.status === 'completed')
        .reduce((sum, txn) => sum + txn.amount, 0),
    [],
  );

  const totalGrowthCredits = useMemo<number>(
    () =>
      sampleDonationTransactions
        .filter((txn) => txn.status === 'completed')
        .reduce((sum, txn) => sum + txn.growthCredits, 0),
    [],
  );

  const settingSections: ISettingSection[] = [
    {
      name: t('Payment processor'),
      description: t('Stripe Connected Account, payout schedule, and disbursement settings.'),
    },
    {
      name: t('Donation defaults'),
      description: t('Suggested amounts, recurring giving cadence, and minimum gift.'),
    },
    {
      name: t('Receipts & confirmations'),
      description: t('Donor receipt template, confirmation page, and reply-to address.'),
    },
    {
      name: t('Branding'),
      description: t('Logo, brand colors, and donation page theme.'),
    },
    {
      name: t('Tax & compliance'),
      description: t('501(c)(3) status, EIN, and donor disclosure language.'),
    },
  ];

  const filterElements = [
    <Box key={'search'} w={320}>
      <Input
        isClearable={true}
        onChange={(next?: string): void => setSearch(next ?? '')}
        placeholder={tab === 'activity' ? t('Search donors or forms…') : t('Search by name…')}
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        type={'text'}
        value={search}
      />
    </Box>,
    <Toolbar align={'right'} key={'filters'}>
      <Button prefix={<FontAwesomeIcon icon={faSliders} />} type={'secondary'}>
        {t('Filters')}
      </Button>
      <Button prefix={<FontAwesomeIcon icon={faTableColumns} />} type={'secondary'}>
        {t('Customize columns')}
      </Button>
    </Toolbar>,
  ];

  function renderActivityActions(): JSX.Element {
    return (
      <Toolbar>
        <Button prefix={<FontAwesomeIcon icon={faDownload} />} type={'secondary'}>
          {t('Export as CSV')}
        </Button>
      </Toolbar>
    );
  }

  function renderFormsActions(): JSX.Element {
    return (
      <Toolbar>
        <Button prefix={<FontAwesomeIcon icon={faDownload} />} type={'secondary'}>
          {t('Export as CSV')}
        </Button>
        <Button
          onClick={(): void => {
            setDoneTitle(t('New donation form'));
            setDoneOpen(true);
          }}
          prefix={<FontAwesomeIcon icon={faPlus} />}
          type={'primary'}
        >
          {t('New donation form')}
        </Button>
      </Toolbar>
    );
  }

  let pageActions: JSX.Element | undefined;
  if (tab === 'activity') {
    pageActions = renderActivityActions();
  } else if (tab === 'forms') {
    pageActions = renderFormsActions();
  }

  return (
    <>
      <PageHeader
        actions={pageActions}
        BreadcrumbsComponent={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        title={getTitle(t, tab)}
      />
      <div className={shellStyles.body}>
        {tab === 'activity' && (
          <Stack gap={theme.spacing['4']}>
            <SimpleGrid cols={2} spacing={theme.spacing['4']}>
              <CardV2 width={'full'}>
                <CardV2.Content>
                  <Stack gap={theme.spacing['2']}>
                    <Text variant={'smallRegular'}>{t('Total Donation Volume')}</Text>
                    <Heading order={2} variant={'h2'}>
                      {`$${totalDonationVolume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </Heading>
                  </Stack>
                </CardV2.Content>
              </CardV2>
              <CardV2 width={'full'}>
                <CardV2.Content>
                  <Stack gap={theme.spacing['2']}>
                    <Text variant={'smallRegular'}>{t('Total Growth Credits Earned')}</Text>
                    <Heading order={2} variant={'h2'}>
                      {totalGrowthCredits.toLocaleString()}
                    </Heading>
                  </Stack>
                </CardV2.Content>
              </CardV2>
            </SimpleGrid>
            <Table<ISampleDonationTransaction>
              columns={transactionCols}
              filterElements={filterElements}
              filterLabel={<></>}
              idKey={'id'}
              isPaginated={false}
              items={filteredTransactions}
            />
          </Stack>
        )}
        {tab === 'forms' && (
          <Table<ISampleForm>
            columns={formCols}
            filterElements={filterElements}
            filterLabel={<></>}
            idKey={'id'}
            isPaginated={false}
            items={filteredForms}
          />
        )}
        {tab === 'settings' && (
          <Stack gap={theme.spacing['4']}>
            {settingSections.map((s) => (
              <CardV2 key={s.name} width={'full'}>
                <CardV2.Content title={s.name}>
                  <Text variant={'smallRegular'}>{s.description}</Text>
                </CardV2.Content>
              </CardV2>
            ))}
          </Stack>
        )}
      </div>

      <PrototypeDoneModal
        onClose={(): void => setDoneOpen(false)}
        opened={isDoneOpen}
        title={doneTitle ?? t('Coming soon')}
      />
    </>
  );
}

export default FundraisingPage;
